import { Link } from "react-router-dom";
import { appPath } from "@/constants/paths";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { WidgetContentSkeleton } from "@/components/shared/loading-skeleton";
import { FileText, ArrowLeft } from "lucide-react";
import type { ContentReport } from "@/types/report";
import { formatArabicNumber } from "@/lib/formatters";

interface ContentStatusWidgetProps {
  data: ContentReport | undefined;
  isLoading: boolean;
}

export function ContentStatusWidget({ data, isLoading }: ContentStatusWidgetProps) {
  const { has } = useUserPermissions();
  const rows = [
    { label: "الصفحات", value: data?.pages ?? 0 },
    { label: "الأخبار", value: data?.news ?? 0 },
    { label: "المقالات", value: data?.articles ?? 0 },
  ];

  return (
    <Card className="flex flex-col overflow-hidden" dir="rtl">
      <div className="accent-top-bar" />
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">حالة المحتوى</CardTitle>
          <div className="rounded-md bg-primary/10 p-1.5 text-primary">
            <FileText className="size-4" />
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 space-y-4">
        {isLoading ? (
          <WidgetContentSkeleton rows={3} />
        ) : (
          <>
            <div className="flex justify-start gap-3">
              <div className="flex items-center gap-1.5 text-sm">
                <span className="text-muted-foreground">منشور</span>
                <Badge variant="default" className="rounded-full px-2">
                  {formatArabicNumber(data?.totalPublished ?? 0)}
                </Badge>
              </div>
              <div className="flex items-center gap-1.5 text-sm">
                <span className="text-muted-foreground">قيد المراجعة</span>
                <Badge variant="secondary" className="rounded-full px-2">
                  {formatArabicNumber(data?.totalUnpublished ?? 0)}
                </Badge>
              </div>
            </div>

            <div className="space-y-2">
              {rows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between border-b border-border/60 py-1 text-sm last:border-0"
                >
                  <span className="text-muted-foreground">{row.label}</span>
                  <span className="font-medium text-foreground">
                    {formatArabicNumber(row.value)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>

      {has("content:edit") && (
        <CardFooter className="justify-start pt-0">
          <Link
            to={appPath("pages")}
            className="flex items-center gap-1 text-sm text-primary hover:text-primary/80"
          >
            عرض كل المحتوى
            <ArrowLeft className="size-4" />
          </Link>
        </CardFooter>
      )}
    </Card>
  );
}
